import { CreateDeviceRequest, DeviceDTO, HoodType, UpdateDeviceRequest } from '@/models';
import { useDeviceStore, useInstitutionStore, useLabStore, useUIStore } from '@/store';
import React, { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Manages device CRUD operations, pagination, and modal state
 * for the Device Management page
 */
export const useDeviceManagement = () => {
  const {
    devices,
    pagination,
    isLoading,
    error,
    fetchDevices,
    createDevice,
    updateDevice,
    deleteDevice,
    clearError,
  } = useDeviceStore();

  const { institutions, fetchInstitutions } = useInstitutionStore();
  const { labs, fetchLabs } = useLabStore();
  const { addNotification } = useUIStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedDevice, setSelectedDevice] = useState<DeviceDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<DeviceDTO | null>(null);
  const [formData, setFormData] = useState<CreateDeviceRequest>({
    institutionId: '',
    labId: '',
    serialNumber: '',
    model: '',
    macAddress: '',
    identifier: '',
    hoodType: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchDevices(currentPage, pageSize);
    fetchInstitutions(1, 100);
    fetchLabs(1, 100);
  }, [currentPage, pageSize, fetchDevices, fetchInstitutions, fetchLabs]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setModalMode('create');
    setSelectedDevice(null);
    setFormData({
      institutionId: '',
      labId: undefined,
      serialNumber: '',
      model: '',
      macAddress: '',
      identifier: '',
      hoodType: null,
    });
    setShowModal(true);
    clearError();
  }, [clearError]);

  const handleEditClick = useCallback(
    (device: DeviceDTO) => {
      setModalMode('edit');
      setSelectedDevice(device);
      setFormData({
        institutionId: device.institutionId,
        labId: device.labId || undefined,
        serialNumber: device.serialNumber || '',
        model: device.model || '',
        macAddress: device.macAddress || '',
        identifier: device.identifier || '',
        hoodType: device.hoodType ?? null,
      });
      setShowModal(true);
      clearError();
    },
    [clearError]
  );

  const handleDeleteClick = useCallback(
    (device: DeviceDTO) => {
      setDeviceToDelete(device);
      setShowDeleteModal(true);
      clearError();
    },
    [clearError]
  );

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await createDevice(formData);
        addNotification({
          type: 'success',
          message: 'Device created successfully',
          duration: 2000,
        });
      } else if (selectedDevice) {
        const updateData: UpdateDeviceRequest = {
          institutionId: selectedDevice.institutionId,
          labId: formData.labId,
          serialNumber: formData.serialNumber,
          model: formData.model,
          macAddress: formData.macAddress,
          identifier: formData.identifier,
          hoodType: formData.hoodType,
          hardwareVersion: selectedDevice.hardwareVersion,
          firmwareVersion: selectedDevice.firmwareVersion,
          osVersion: selectedDevice.osVersion,
          loggingLevel: selectedDevice.loggingLevel,
          clientAppVersion: selectedDevice.clientAppVersion,
          apiVersion: selectedDevice.apiVersion,
          styleJson: selectedDevice.styleJson,
          timezoneId: selectedDevice.timezoneId,
        };
        await updateDevice(selectedDevice.id, updateData);
        addNotification({
          type: 'success',
          message: 'Device updated successfully',
          duration: 2000,
        });
      }
      setShowModal(false);
      await fetchDevices(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    modalMode,
    createDevice,
    formData,
    selectedDevice,
    updateDevice,
    fetchDevices,
    currentPage,
    pageSize,
    addNotification,
  ]);

  const handleDelete = useCallback(async () => {
    if (!deviceToDelete) return;
    setIsSaving(true);
    try {
      await deleteDevice(deviceToDelete.id);
      addNotification({ type: 'success', message: 'Device deleted successfully', duration: 2000 });
      setShowDeleteModal(false);
      await fetchDevices(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [deviceToDelete, deleteDevice, fetchDevices, currentPage, pageSize, addNotification]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      if (name === 'labId') {
        setFormData((prev) => ({ ...prev, labId: value || undefined }));
      } else if (name === 'hoodType') {
        setFormData((prev) => ({ ...prev, hoodType: (value as HoodType) || null }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    []
  );

  const handleInstitutionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, institutionId: e.target.value, labId: undefined }));
  }, []);

  const closeModal = useCallback(() => setShowModal(false), []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  return {
    devices,
    institutions,
    labs,
    pagination,
    isLoading,
    error,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showModal,
    modalMode,
    showDeleteModal,
    deviceToDelete,
    formData,
    isSaving,
    setCurrentPage,
    handlePageSizeChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleSubmit,
    handleDelete,
    handleInputChange,
    handleInstitutionChange,
    closeModal,
    closeDeleteModal,
    clearError,
  };
};
